import { Avatar } from '@mui/material';
import { Check } from 'lucide-react';
import { getInitials } from '@/utils/getInitials';
import { Select } from '@/components/common/SelectField';
import { User } from '@/_models/User';
import { extractContentFromHTML } from '@/utils/removeHTMLTagsFromString';

type Props = {
  collaborators: User[];
  description: string;
  anchorEl: HTMLElement;
  setAnchorEl: (value: HTMLElement) => void;
  quill: any;
};

export const CollaboratorsList = ({
  collaborators,
  description,
  anchorEl,
  setAnchorEl,
  quill,
}: Props) => {
  const handleCollaboratorSelect = (collaborator: User) => {
    const tag = `@${collaborator.name}`;
    const selection = quill.getSelection();
    const index = selection ? selection.index : quill.getLength();

    if (quill) {
      const currentText = quill.getText();
      const lastAtIndex = currentText.lastIndexOf('@');
      const containsAt = lastAtIndex >= 0 && index - lastAtIndex <= 2;
      const mentionHtml = `<span style="color:blue">${tag}</span>&nbsp;`;

      if (containsAt) {
        const startIndex = lastAtIndex;
        const endIndex = index;

        quill.deleteText(startIndex, endIndex - startIndex);

        quill.clipboard.dangerouslyPasteHTML(startIndex, mentionHtml);
        quill.setSelection(startIndex + tag.length + 6);
      } else {
        quill.clipboard.dangerouslyPasteHTML(index, mentionHtml);
        quill.setSelection(index + tag.length + 6);
      }

      setAnchorEl(null);
    }
  };

  const open = Boolean(anchorEl);

  return (
    <Select
      open={open}
      onOpenChange={(v) => setAnchorEl(v ? anchorEl : null)}
      anchorEl={anchorEl}
      options={collaborators.map((collaborator) => {
        // if we just check if description includes tagged collaborator name, it will not work if the name contains spaces
        // because of different spaces used in HTML and plain text
        // so we need to normalize spaces in both description and collaborator name before comparing them

        const tagPattern = `@${collaborator.name}`;
        const plainTextDescription = extractContentFromHTML(description);

        const normalizeSpaces = (text: string) => {
          return text
            .replace(/[\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]/g, ' ') // Replace various Unicode spaces with regular space
            .replace(/\s+/g, ' ') // Replace multiple spaces with single space
            .trim();
        };

        const normalizedDescription = normalizeSpaces(plainTextDescription.toLowerCase());
        const normalizedPattern = normalizeSpaces(tagPattern.toLowerCase());
        const isTagged = normalizedDescription.includes(normalizedPattern);

        return {
          value: collaborator.id,
          label: collaborator.name,
          icon: (
            <Avatar
              sx={{
                bgcolor: '#2055ff',
                color: 'white',
                border: '1px solid #D1D1D1',
                height: '25px',
                width: '25px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
              }}
            >
              {getInitials(collaborator?.name || 'Guest')}
            </Avatar>
          ),
          suffix: isTagged && (
            <Check size={16} style={{ color: '#2055ff', marginLeft: 'auto' }} />
          ),
        };
      })}
      hideTrigger
      menuProps={{
        PaperProps: {
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
        },
      }}
      menuWidth="250px"
      searchable
      onChange={({ value }) => {
        const collaborator = collaborators.find((c) => c.id === value);
        handleCollaboratorSelect(collaborator);
      }}
    />
  );
};
